import * as Sentry from "@sentry/react";

/**
 * Sentry Breadcrumb Tracking Utilities
 * These functions add breadcrumbs to help debug errors by tracking user actions
 */

export const trackNavigation = (from: string, to: string) => {
  Sentry.addBreadcrumb({
    category: "navigation",
    message: `Navigated from ${from} to ${to}`,
    level: "info",
    data: {
      from,
      to,
      timestamp: new Date().toISOString(),
    },
  });
};

export const trackButtonClick = (
  buttonName: string,
  context?: Record<string, any>
) => {
  Sentry.addBreadcrumb({
    category: "ui.click",
    message: `Button clicked: ${buttonName}`,
    level: "info",
    data: {
      button: buttonName,
      ...context,
      timestamp: new Date().toISOString(),
    },
  });
};

export const trackFormSubmission = (
  formName: string,
  success: boolean,
  context?: Record<string, any>
) => {
  Sentry.addBreadcrumb({
    category: "ui.form",
    message: `Form ${success ? "submitted" : "failed"}: ${formName}`,
    level: success ? "info" : "warning",
    data: {
      form: formName,
      success,
      ...context,
      timestamp: new Date().toISOString(),
    },
  });
};

export const trackApiCall = (
  endpoint: string,
  method: string,
  status?: number,
  error?: string
) => {
  Sentry.addBreadcrumb({
    category: "api",
    message: `API ${method} ${endpoint}`,
    level: error ? "error" : "info",
    data: {
      endpoint,
      method,
      status,
      error,
      timestamp: new Date().toISOString(),
    },
  });
};

export const trackEdgeFunctionCall = (
  functionName: string,
  success: boolean,
  duration?: number,
  error?: string
) => {
  Sentry.addBreadcrumb({
    category: "edge_function",
    message: `Edge function ${success ? "called" : "failed"}: ${functionName}`,
    level: success ? "info" : "error",
    data: {
      function: functionName,
      success,
      duration_ms: duration,
      error,
      timestamp: new Date().toISOString(),
    },
  });
};

export const trackDatabaseQuery = (
  table: string,
  operation: string,
  success: boolean,
  error?: string
) => {
  Sentry.addBreadcrumb({
    category: "database",
    message: `Database ${operation} on ${table}`,
    level: success ? "info" : "error",
    data: {
      table,
      operation,
      success,
      error,
      timestamp: new Date().toISOString(),
    },
  });
};

export const trackUserAction = (
  action: string,
  category: string,
  context?: Record<string, any>
) => {
  Sentry.addBreadcrumb({
    category: `user.${category}`,
    message: `User action: ${action}`,
    level: "info",
    data: {
      action,
      ...context,
      timestamp: new Date().toISOString(),
    },
  });
};

export const trackAIGeneration = (
  model: string,
  operation: string,
  success: boolean,
  duration?: number,
  tokens?: number
) => {
  Sentry.addBreadcrumb({
    category: "ai.generation",
    message: `AI ${operation} with ${model}`,
    level: success ? "info" : "warning",
    data: {
      model,
      operation,
      success,
      duration_ms: duration,
      tokens,
      timestamp: new Date().toISOString(),
    },
  });
};

export const trackSearchQuery = (
  query: string,
  resultsCount: number,
  filters?: Record<string, any>
) => {
  Sentry.addBreadcrumb({
    category: "search",
    message: `Search query: "${query}"`,
    level: "info",
    data: {
      query,
      results: resultsCount,
      filters,
      timestamp: new Date().toISOString(),
    },
  });
};

export const trackDataChange = (
  entity: string,
  action: "create" | "update" | "delete",
  entityId?: string
) => {
  Sentry.addBreadcrumb({
    category: "data.change",
    message: `${action} ${entity}`,
    level: "info",
    data: {
      entity,
      action,
      entity_id: entityId,
      timestamp: new Date().toISOString(),
    },
  });
};
